/**
 * GazettePhotoManager — Upload local + Google Photos Picker
 * Flow Picker: crée session → ouvre popup Google → poll → import photos
 */
import React, { useState, useEffect, useRef } from 'react';
import {
  Upload, Trash2, Image, Loader, Cloud, X
} from 'lucide-react';
import {
  uploadGazettePhotos, deleteGazettePhoto, updateGazettePhoto,
  getGooglePhotosStatus, createGooglePickerSession, pollGooglePickerSession, importGooglePickerPhotos
} from '../../api';
import { useToast } from '../../contexts/NotificationContext';

export default function GazettePhotoManager({ gazetteId, photos = [], sectionItems = [], onRefresh }) {
  const toast = useToast();
  const [uploading, setUploading] = useState(false);
  const [googleStatus, setGoogleStatus] = useState(null);
  const [pickerLoading, setPickerLoading] = useState(null); // 'bibou' | 'choupi' | null
  const [pickerStatus, setPickerStatus] = useState(''); // status message
  const pollingRef = useRef(null);

  useEffect(() => {
    getGooglePhotosStatus().then(r => setGoogleStatus(r.data)).catch(() => {});
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, []);

  // --- Local upload ---
  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const formData = new FormData();
    Array.from(files).forEach(f => formData.append('photos', f));

    try {
      setUploading(true);
      await uploadGazettePhotos(gazetteId, formData);
      toast.success(`${files.length} photo(s) ajoutée(s)`);
      onRefresh();
    } catch (err) {
      toast.error('Erreur upload photos');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  // --- Delete photo ---
  const handleDelete = async (photoId) => {
    if (!window.confirm('Supprimer cette photo ?')) return;
    try {
      await deleteGazettePhoto(photoId);
      toast.success('Photo supprimée');
      onRefresh();
    } catch (err) {
      toast.error('Erreur suppression');
    }
  };

  // --- Associate photo to item ---
  const handleAssociate = async (photoId, sectionItemId) => {
    try {
      await updateGazettePhoto(photoId, { sectionItemId: sectionItemId || null });
      onRefresh();
    } catch (err) {
      toast.error('Erreur association');
    }
  };

  // --- Google Photos Picker flow ---
  const openPicker = async (structureKey) => {
    try {
      setPickerLoading(structureKey);
      setPickerStatus('Création de la session...');

      // 1. Create picker session
      const { data: session } = await createGooglePickerSession(structureKey);

      // 2. Open picker in popup
      setPickerStatus('Sélectionnez vos photos dans le popup Google...');
      const pickerWindow = window.open(session.pickerUri, '_blank', 'width=1024,height=700,scrollbars=yes');

      // 3. Poll for completion
      pollingRef.current = setInterval(async () => {
        try {
          const { data: pollResult } = await pollGooglePickerSession(structureKey, session.sessionId);

          if (pollResult.mediaItemsSet) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;

            setPickerStatus('Téléchargement des photos sélectionnées...');

            // 4. Import photos
            const { data: importResult } = await importGooglePickerPhotos(structureKey, session.sessionId, gazetteId);

            setPickerLoading(null);
            setPickerStatus('');

            if (importResult.imported > 0) {
              toast.success(`${importResult.imported} photo(s) importée(s) depuis Google Photos`);
              onRefresh();
            } else {
              toast.info('Aucune photo sélectionnée');
            }

            if (pickerWindow && !pickerWindow.closed) pickerWindow.close();
          }
        } catch (pollErr) {
          console.error('Polling error:', pollErr);
        }
      }, 2000);

      // Detect if user closes popup without selecting
      const checkClosed = setInterval(() => {
        if (pickerWindow && pickerWindow.closed) {
          clearInterval(checkClosed);
          setTimeout(() => {
            if (pollingRef.current) {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
              setPickerLoading(null);
              setPickerStatus('');
            }
          }, 5000); // Grace period for last poll
        }
      }, 1000);

    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur Google Photos Picker');
      setPickerLoading(null);
      setPickerStatus('');
    }
  };

  // All items for association dropdown
  const allItems = sectionItems.flatMap(section =>
    (section.items || []).map(item => ({
      id: item.id,
      label: `${section.sectionType?.emoji || '📄'} ${item.title || 'Sans titre'}`,
    }))
  );

  return (
    <div className="space-y-4">
      {/* Upload bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <label className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg text-sm hover:bg-primary-dark cursor-pointer transition-colors">
          <Upload size={16} />
          {uploading ? 'Upload...' : 'Ajouter des photos'}
          <input
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp"
            onChange={handleUpload}
            className="hidden"
            disabled={uploading}
          />
        </label>

        {/* Google Photos Picker buttons */}
        {googleStatus?.bibou && (
          <button
            onClick={() => openPicker('bibou')}
            disabled={!!pickerLoading}
            className="flex items-center gap-2 border border-blue-300 text-blue-700 px-3 py-2 rounded-lg text-sm hover:bg-blue-50 disabled:opacity-50"
          >
            {pickerLoading === 'bibou' ? <Loader size={16} className="animate-spin" /> : <Cloud size={16} />}
            Google Photos Bibou
          </button>
        )}
        {googleStatus?.choupi && (
          <button
            onClick={() => openPicker('choupi')}
            disabled={!!pickerLoading}
            className="flex items-center gap-2 border border-purple-300 text-purple-700 px-3 py-2 rounded-lg text-sm hover:bg-purple-50 disabled:opacity-50"
          >
            {pickerLoading === 'choupi' ? <Loader size={16} className="animate-spin" /> : <Cloud size={16} />}
            Google Photos Choupi
          </button>
        )}

        <span className="text-xs text-gray-400">{photos.length} photo(s)</span>
      </div>

      {/* Picker status */}
      {pickerStatus && (
        <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 text-sm text-blue-700">
          <Loader size={16} className="animate-spin" />
          {pickerStatus}
          <button
            onClick={() => {
              if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
              setPickerLoading(null);
              setPickerStatus('');
            }}
            className="ml-auto text-blue-400 hover:text-blue-600"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Photo grid */}
      {photos.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {photos.map(photo => (
            <div key={photo.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden group">
              <div className="relative">
                <img
                  src={photo.fileUrl}
                  alt={photo.caption || photo.fileName}
                  className="w-full h-28 object-cover"
                />
                {photo.source === 'google_photos' && (
                  <div className="absolute top-1 left-1">
                    <Cloud size={14} className="text-white drop-shadow" />
                  </div>
                )}
                <button
                  onClick={() => handleDelete(photo.id)}
                  className="absolute top-1 right-1 w-6 h-6 bg-red-500/80 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Trash2 size={12} className="text-white" />
                </button>
              </div>
              <div className="p-1.5">
                <select
                  value={photo.sectionItemId || ''}
                  onChange={e => handleAssociate(photo.id, e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full text-xs border rounded px-1.5 py-1 text-gray-600"
                >
                  <option value="">Non assignée</option>
                  {allItems.map(item => (
                    <option key={item.id} value={item.id}>{item.label}</option>
                  ))}
                </select>
              </div>
            </div>
          ))}
        </div>
      )}

      {photos.length === 0 && !pickerStatus && (
        <div className="text-center py-8 text-gray-400 border border-dashed border-gray-200 rounded-xl">
          <Image size={36} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">Aucune photo — uploadez ou importez depuis Google Photos</p>
        </div>
      )}
    </div>
  );
}
